import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, PartyPopper, TreePine, UtensilsCrossed, X } from 'lucide-react'

const STORAGE_KEY = 'hm-festtage-2026-v2'
const CAMPAIGN_END = new Date('2027-01-02T00:00:00')

const offers = [
  {
    href: '/brunch?kategorie=weihnachten',
    icon: TreePine,
    eyebrow: '6., 25. und 26. Dez.',
    title: 'Weihnachtsbrunch',
    detail: 'Termine ansehen',
  },
  {
    href: '/brunch/silvester-dinnerbuffet',
    icon: UtensilsCrossed,
    eyebrow: '31. Dez.',
    title: 'Silvester Dinnerbuffet',
    detail: 'Party-Eintritt kostenlos',
  },
  {
    href: '/partys/die-gro-e-h-ckels-may-silvester-party',
    icon: PartyPopper,
    eyebrow: '31. Dez.',
    title: 'Silvesterparty',
    detail: 'Mehr erfahren',
  },
]

export default function FesttagePopup() {
  const [open, setOpen] = useState(false)
  const [showPhoto, setShowPhoto] = useState(true)
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1')
    } catch {
      // Privater Modus: Einladung bleibt bis zum nächsten Laden sichtbar.
    }
    setOpen(false)
  }, [])

  useEffect(() => {
    if (new Date() >= CAMPAIGN_END) return
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') return
    } catch {
      // Ohne Speicher trotzdem einmal zeigen.
    }
    const timer = window.setTimeout(() => setOpen(true), 700)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null

    const html = document.documentElement
    const body = document.body
    const scrollY = window.scrollY
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
    }

    html.dataset.festtageLock = '1'
    html.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'none'
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'

    const dialogCanScroll = () => {
      const dialog = dialogRef.current
      return !!dialog && dialog.scrollHeight > dialog.clientHeight + 1
    }

    const eventInsideDialog = (event: Event) => {
      const dialog = dialogRef.current
      return !!dialog && event.target instanceof Node && dialog.contains(event.target)
    }

    const onWheel = (event: WheelEvent) => {
      const dialog = dialogRef.current
      if (eventInsideDialog(event) && dialog && dialogCanScroll()) {
        const atTop = dialog.scrollTop <= 0 && event.deltaY < 0
        const atBottom = dialog.scrollTop + dialog.clientHeight >= dialog.scrollHeight - 1 && event.deltaY > 0
        if (!atTop && !atBottom) return
      }
      event.preventDefault()
    }

    const onTouchMove = (event: TouchEvent) => {
      if (eventInsideDialog(event) && dialogCanScroll()) return
      event.preventDefault()
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss()
        return
      }
      const root = dialogRef.current
      if (event.key === 'Tab' && root) {
        const items = Array.from(
          root.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
        )
        if (items.length === 0) return
        const first = items[0]
        const last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
        return
      }
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']
      if (!scrollKeys.includes(event.key)) return
      const tag = event.target instanceof HTMLElement ? event.target.tagName : ''
      if (event.key === ' ' && (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'TEXTAREA')) return
      if (eventInsideDialog(event) && dialogCanScroll()) return
      event.preventDefault()
    }

    document.addEventListener('wheel', onWheel, { capture: true, passive: false })
    document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false })
    document.addEventListener('keydown', onKey)
    dialogRef.current?.focus()

    return () => {
      delete html.dataset.festtageLock
      html.style.overflow = previous.htmlOverflow
      html.style.overscrollBehavior = previous.htmlOverscroll
      body.style.overflow = previous.bodyOverflow
      body.style.overscrollBehavior = previous.bodyOverscroll
      body.style.position = previous.bodyPosition
      body.style.top = previous.bodyTop
      body.style.left = previous.bodyLeft
      body.style.right = previous.bodyRight
      body.style.width = previous.bodyWidth
      window.scrollTo(0, scrollY)
      document.removeEventListener('wheel', onWheel, true)
      document.removeEventListener('touchmove', onTouchMove, true)
      document.removeEventListener('keydown', onKey)
      previouslyFocused.current?.focus()
    }
  }, [open, dismiss])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-gray-950/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={dismiss}
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="festtage-title"
            aria-describedby="festtage-text"
            tabIndex={-1}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
            className="festtage-popup relative w-full max-w-4xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-3xl bg-gray-950 border border-white/15 shadow-2xl outline-none"
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500 to-transparent z-20" />

            <button
              type="button"
              onClick={dismiss}
              className="absolute top-3 right-3 z-20 p-2 rounded-full bg-gray-950/90 border border-white/20 text-white hover:bg-gray-900 transition-colors"
              aria-label="Einladung schließen"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              {showPhoto && (
                <div className="relative h-24 sm:h-28 overflow-hidden">
                  <img
                    src="/assets/brunch/seite/weihnachten_brunchseite.jpg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover object-[center_40%]"
                    onError={() => setShowPhoto(false)}
                  />
                  <div className="absolute inset-0 bg-gray-950/0 bg-gradient-to-t from-[#0f0f0f] via-[#0f0f0f]/55 to-black/20" />
                </div>
              )}

              <div className={`px-5 pb-4 sm:px-6 sm:pb-5 ${showPhoto ? '-mt-6 relative' : 'pt-12'}`}>
                <p className="text-brand-400 text-[11px] uppercase tracking-[0.35em] mb-1.5">
                  Festtage 2026
                </p>
                <h2
                  id="festtage-title"
                  className="font-serif text-2xl md:text-3xl text-white font-medium leading-tight mb-2"
                >
                  Weihnachtsbrunch & Silvester
                </h2>
                <p id="festtage-text" className="text-white/55 text-sm leading-relaxed mb-4 max-w-3xl">
                  Weihnachtsbrunch an Nikolaus und an beiden Weihnachtsfeiertagen.
                  Am 31. Dezember gibt es Silvester Dinnerbuffet und Silvesterparty, zwei eigene Abende.
                  Für Gäste des Dinners ist der Eintritt zur Party kostenlos.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  {offers.map((offer) => {
                    const Icon = offer.icon
                    const inner = (
                      <>
                        <div className="w-9 h-9 mb-3 rounded-xl bg-brand-800/15 border border-brand-800/30 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-brand-400" />
                        </div>
                        <p className="text-brand-400 text-xs mb-1">
                          {offer.eyebrow}
                        </p>
                        <p className="font-serif text-xl text-white font-medium leading-tight">{offer.title}</p>
                        <p className="mt-1 text-white/50 text-sm leading-snug inline-flex items-center gap-1.5">
                          {offer.detail}
                          {offer.href && (
                            <ArrowRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                          )}
                        </p>
                      </>
                    )
                    const className = 'group block rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-left'

                    if (!offer.href) {
                      return (
                        <div key={offer.title} className={className}>
                          {inner}
                        </div>
                      )
                    }

                    return (
                      <Link
                        key={offer.href}
                        to={offer.href}
                        onClick={dismiss}
                        className={`${className} hover:border-brand-400/60 hover:bg-white/[0.09] transition-colors`}
                      >
                        {inner}
                      </Link>
                    )
                  })}
                </div>

                <button
                  type="button"
                  onClick={dismiss}
                  className="px-1 py-2 text-white/40 hover:text-white/70 text-xs uppercase tracking-[0.18em] transition-colors"
                >
                  Schließen
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
